import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { push } from "redux-first-history";
import { takeEvery, put, call, all, Effect, takeLatest, select } from "redux-saga/effects";
import { taxApi as TaxApi } from "../../api/TaxApi";
import { IRS1040Form, Save1040Response, Uploaded1040Document } from "../../model/tax/irs1040";
import { IRSW2Form, SaveW2Response, SaveW2CollectionResponse, LocalWithholding } from "../../model/tax/irsw2";
import { RootState } from "..";
import { TaxDocument } from "../../model/tax/tax_document";
import { TaxDocumentStatus } from "../../enums/tax";
import { FormTypes } from "../../utils/models";
import { Confirm1040Task, ConfirmSchedule1Task, ConfirmScheduleCTask, ConfirmW2Task, Delete1040Task, DeleteSchedule1Task, DeleteScheduleCTask, DeleteW2Task, Upload1040Task, UploadSchedule1Task, UploadScheduleCTask, UploadW2Task, Validate1040IncomeTask } from "../../utils/tasks";
import { taxSlice } from "../reducer/tax";
import { TaxReturn, TaxReturnStatusResponse } from "../../model/tax/tax_return";
import { IRSSchedule1Form, SaveSchedule1CollectionResponse, SaveSchedule1Response } from "../../model/tax/irsschedule1";
import { ApiResponse } from "../../model/api";
import { IRSScheduleCForm, SaveScheduleCCollectionResponse, SaveScheduleCResponse } from "../../model/tax/irsschedulec";
import { omitDollar, toIntString, toIntStringND, toNumber } from "../../utils/dataConverter";
import { detectMissingLocalWithholdings, detectMissingBox18 } from "../../utils/detectLocalWithholdings";
import { FormW2ErrorModel } from "../../model/tax/error";
import { ConfirmW2Params } from "../model/tax";
import { ApiError, SerializedApiError } from "../../model/errors/errors";
import { typeNameMap } from "../../containers/Upload/UploadContainer";

export const knownBadLocalities = [
  'total',
  'city',
  'total city'
];

function* fetchTaxReturnSaga(): Generator<Effect, void, any> {
  try {
    const response = (yield call(TaxApi.fetchTaxReturn)) as ApiResponse<TaxReturnStatusResponse>;
    yield put(taxSlice.actions.setTaxReturn(response.data.tax_return_status));
  } catch (error: SerializedApiError<any> | any) {
    yield put(push("/upload/sorry", { state: { error: error } }));
  }
}

function* fetch1040FormDetailsSaga(): Generator<Effect, void, any> {
  try {
    const response = (yield call(TaxApi.fetch1040FormDetails)) as ApiResponse<Uploaded1040Document>;
    yield put(taxSlice.actions.fetch1040FormDetailsSuccess(response.data));
  } catch (error: SerializedApiError<any> | any) {
    yield put(taxSlice.actions.delete1040Success());
  }
}

function* fetchW2FormDetailsSaga(): Generator<Effect, void, any> {
  try {
    const response = yield call(TaxApi.fetchW2FormDetails);
    yield put(taxSlice.actions.fetchW2FormDetailsSuccess(response.data));
  } catch (error: SerializedApiError<any> | any) {
    yield put(taxSlice.actions.fetchW2FormDetailsError({message: error.toString()}));
  }
}

function* fetchSchedule1FormDetailsSaga(): Generator<Effect, void, any> {
  try {
    const response = yield call(TaxApi.fetchSchedule1FormDetails);
    yield put(taxSlice.actions.fetchSchedule1FormDetailsSuccess(response.data));
  } catch (error: SerializedApiError<any> | any) {
    yield put(taxSlice.actions.fetchSchedule1FormDetailsError({ 
      message: ApiError.deserialize(error)?.toString() ?? String(error)
    }));
  }
}

function* fetchScheduleCFormDetailsSaga(): Generator<Effect, void, any> {
  try {
    const response = yield call(TaxApi.fetchScheduleCFormDetails);
    yield put(taxSlice.actions.fetchScheduleCFormDetailsSuccess(response.data));
  } catch (error: any) {
    yield put(taxSlice.actions.fetchScheduleCFormDetailsError({
      message: ApiError.deserialize(error)?.toString() ?? String(error)
    }));
  }
}

function* validate1040IncomeSaga(): Generator<Effect, void, any> {
  yield put(taxSlice.actions.addTask(Validate1040IncomeTask));
  const irs1040 = (yield select(
    (state: RootState) => state.tax.irs1040)) as TaxDocument<IRS1040Form>;

  const wages_salary_tips = toIntString(irs1040.form_data.wages_salary_tips);
  if (Number(wages_salary_tips) == 0) {
    yield put(taxSlice.actions.show1040NoIncomePrompt(true));
  } else {
    yield put(taxSlice.actions.show1040NoIncomePrompt(false));
    yield put(taxSlice.actions.confirm1040Request());
  }
  
  yield put(taxSlice.actions.removeTask(Validate1040IncomeTask.id));
}

function* confirm1040Saga(): Generator<Effect, void, any> {
  yield put(taxSlice.actions.addTask(Confirm1040Task));
  try {
    const irs1040 = (yield select(
      (state: RootState) => state.tax.irs1040)) as TaxDocument<IRS1040Form>;

    const wages_salary_tips = toIntString(irs1040.form_data.wages_salary_tips);
    const response = (yield call(TaxApi.confirmDocument, {
      ...irs1040,
      form_data: {
        wages_salary_tips: wages_salary_tips,
        other_income: toIntString(irs1040.form_data.other_income)
      }
    })) as ApiResponse<Save1040Response>;
    const taxReturn = response.data.tax_return;
    yield put(taxSlice.actions.setTaxReturn(taxReturn));
    yield put(taxSlice.actions.proceedToNextStep({form: FormTypes.t1040, balance: true}));
  } catch (error: SerializedApiError<any> | any) {
    yield put(push("/upload/sorry", { state: { error: error } }));
  } finally {
    yield put(taxSlice.actions.removeTask(Confirm1040Task.id));
  }
}

function* proceeedToNextStepSaga({ payload }: PayloadAction<{form: FormTypes, balance: boolean}>): Generator<Effect, void, any> {
  const { form, balance } = payload;
  const taxReturn = (yield select(
    (state: RootState) => state.tax.tax_return)) as TaxReturn;
  if (taxReturn[form].status !== TaxDocumentStatus.Complete) {
    return;
  }
  yield put(taxSlice.actions.selectForm(-1));
  const steps = Object.keys(typeNameMap) as FormTypes[];
  let index = steps.indexOf(form);
  const skip = [TaxDocumentStatus.NotRequired, TaxDocumentStatus.Complete];
  const skipStrict = [TaxDocumentStatus.NotRequired];
  while (index < steps.length - 1 && skip.includes(taxReturn[steps[index]].status)) {
    if (taxReturn[steps[index]].status !== TaxDocumentStatus.NotRequired) {
      yield put(push(`/upload/${steps[index]}`));
    }
    index++;
  }
  let proceedToBalance = balance && index === steps.length - 1 && skip.includes(taxReturn[steps[index]].status);
  if (!proceedToBalance && !(skipStrict.includes(taxReturn[steps[index]].status))) {
    yield put(push(`/upload/${steps[index]}`));
  }
  if (balance && index === steps.length - 1 && skip.includes(taxReturn[steps[index]].status)) {
    yield put(push("/balance"));
    return;
  }
}

function* confirmAllW2Saga({ payload }: PayloadAction<ConfirmW2Params>): Generator<Effect, void, any> {

  yield put(taxSlice.actions.addTask(ConfirmW2Task));
  try {
    const irsW2Forms = (yield select(
      (state: RootState) => state.tax.irsW2Forms)) as TaxDocument<IRSW2Form>[];

    if (irsW2Forms.length === 0) {
      return;
    }
    if (!payload.skip_missing_lwh) {
      for (const irsW2 of irsW2Forms) {
        if (detectMissingLocalWithholdings(irsW2)) {
          yield put(taxSlice.actions.toggleShowLocalWithholdingsPrompt(true));
          return;
        }
      }
    }

    for (const irsW2 of irsW2Forms) {
      const wages_tips_other_comp = toNumber(omitDollar(String(irsW2.form_data.wages_tips_other_comp_box1) || "0"));
      const medicare_wages_and_tips = toNumber(omitDollar(String(irsW2.form_data.medicare_wages_and_tips) || "0"));
      const hasInvalidLocality = irsW2.form_data.local_withholdings?.some((locality) => {
        const name = locality.locality_name?.toLowerCase()?.trim();
        return name && knownBadLocalities.includes(name);
      });
      if (hasInvalidLocality) {
        yield put(taxSlice.actions.toggleShowInvalidLocalityPrompt(true));
        return;
      }
      if (medicare_wages_and_tips == 0 || medicare_wages_and_tips < wages_tips_other_comp) {
        yield put(taxSlice.actions.toggleShowInvalidBox5Prompt(true));
        return;
      }
      if (!payload.skip_missing_box18) {
        if (detectMissingBox18(irsW2)) {
          yield put(taxSlice.actions.toggleShowMissingBox18Prompt(true));
          return;
        }
      }
    }
    
    let response, i = 0;
    for (const irsW2 of irsW2Forms) {
      try {
        response = (yield call(TaxApi.confirmDocument, {
          ...irsW2,
          form_data: {
            ...irsW2.form_data,
            employer_tax_id: toIntStringND(irsW2.form_data.employer_tax_id ?? ""),
            wages_tips_other_comp_box1: toIntString(irsW2.form_data.wages_tips_other_comp_box1 ?? ""),
            medicare_wages_and_tips: toIntString(irsW2.form_data.medicare_wages_and_tips ?? ""),
            local_withholdings: (irsW2.form_data.local_withholdings ?? [])
              .filter(lwh => !!lwh.locality_name && (lwh.local_income_tax || lwh.local_wages_and_tips))
              .map(lwh => ({
              ...lwh,
              local_income_tax: toIntString(lwh.local_income_tax),
              local_wages_and_tips: toIntString(lwh.local_wages_and_tips) || 0
            }))
          }
        })) as ApiResponse<SaveW2Response>;
        yield put(taxSlice.actions.confirmW2Success({index: i, document: response.data}));
        i++;
      } catch (error: SerializedApiError<FormW2ErrorModel> | any) {
        const err = ApiError.deserialize<FormW2ErrorModel>(error)?.data;
        if (err && err.errors) {
          for (const [_, errors] of Object.entries((err as FormW2ErrorModel).errors)) {
            let errmsg = errors[0];
            if (errmsg.startsWith("Localities must be listed individually")) {
              errmsg = "You uploaded a W-2 that lists “TOTAL” or “CITY” as the city name in box 20. " + 
              "This W-2 likely has multiple pages that detail the wages you earned and tax " + 
              "you paid to two or more cities in boxes, 18, 19 and 20. Please delete the file " + 
              "you uploaded and, instead, upload one file that includes only the pages of your " + 
              "W-2 that report the wage and withholding information for each taxing city. If " + 
              `you need help, please email ${import.meta.env.VITE_APP_SAAS_CUSTOMER_EMAIL} or call ${import.meta.env.VITE_APP_SAAS_CUSTOMER_PHONE} for assistance.`;
            }
    
            yield put(taxSlice.actions.addW2Message(errmsg));
            return;
          }
        } else {
          throw error;
        }
      }
    }

    const taxReturn = (response as ApiResponse<SaveW2Response>).data.tax_return;
    yield put(taxSlice.actions.setTaxReturn(taxReturn));

    if (taxReturn.IRSW2.status !== TaxDocumentStatus.Complete) {
      return;
    }

    yield put(taxSlice.actions.selectForm(-1));
    yield put(taxSlice.actions.proceedToNextStep({form: FormTypes.w2, balance: true}));

  } catch (error: SerializedApiError<any> | any) {
    console.log(error);
    yield put(push("/upload/sorry", { state: { error: error } }));
  } finally {
    yield put(taxSlice.actions.removeTask(ConfirmW2Task.id));
  }
}

function* confirmAllSchedule1Saga(): Generator<Effect, void, any> {
  yield put(taxSlice.actions.addTask(ConfirmSchedule1Task));
  try {
    const irsSchedule1Forms = (yield select(
      (state: RootState) => state.tax.irsSchedule1Forms)) as TaxDocument<IRSSchedule1Form>[];

    if (irsSchedule1Forms.length === 0) {
      return;
    }
    
    let response, i = 0;
    for (const irsSchedule1 of irsSchedule1Forms) {
      /// Map all null / blank fields to zero
      const formData: any = {};
      for (const [key, value] of Object.entries(irsSchedule1.form_data)) {
        if (value === null || value === "") {
          formData[key] = "0";
        } else {
          formData[key] = value;
        }
      }
      const forUpload = {
        ...irsSchedule1,
        form_data: {
          ...formData,
          business_income_or_loss: toIntString(formData.business_income_or_loss),
          cancellation_of_debt: toIntString(formData.cancellation_of_debt),
          farm_income_or_loss: toIntString(formData.farm_income_or_loss),
          gambling_income: toIntString(formData.gambling_income),
          jury_duty_pay: toIntString(formData.jury_duty_pay),
          other_gains_or_losses: toIntString(formData.other_gains_or_losses),
          other_income: toIntString(formData.other_income),
          rentals_royalties_etc: toIntString(formData.rentals_royalties_etc),
          scholarship_and_grant_income: toIntString(formData.scholarship_and_grant_income)
        }
      }

      response = (yield call(TaxApi.confirmDocument, forUpload)) as ApiResponse<SaveSchedule1Response>;
      yield put(taxSlice.actions.confirmSchedule1Success({index: i, document: response.data}));
      i++;
    }

    const taxReturn = (response as ApiResponse<SaveSchedule1Response>).data.tax_return;
    yield put(taxSlice.actions.setTaxReturn(taxReturn));

    if (taxReturn.IRSSchedule1.status !== TaxDocumentStatus.Complete) {
      return;
    }

    yield put(taxSlice.actions.selectForm(-1));
    yield put(taxSlice.actions.proceedToNextStep({form: FormTypes.schedule1, balance: true}));
  } catch (error: SerializedApiError<any> | any) {
    yield put(push("/upload/sorry", { state: { error: error } }));
  } finally {
    yield put(taxSlice.actions.removeTask(ConfirmSchedule1Task.id));
  }
}

function* confirmAllScheduleCSaga(): Generator<Effect, void, any> {
  yield put(taxSlice.actions.addTask(ConfirmScheduleCTask));
  try {
    const irsScheduleCForms = (yield select(
      (state: RootState) => state.tax.irsScheduleCForms)) as TaxDocument<IRSScheduleCForm>[];

    if (irsScheduleCForms.length === 0) {
      return;
    }
    
    let response, i = 0;
    for (const irsScheduleC of irsScheduleCForms) {
      /// Map all null / blank fields to zero
      const formData: any = {};
      for (const [key, value] of Object.entries(irsScheduleC.form_data)) {
        if (value === null || value === "") {
          formData[key] = "0";
        } else {
          formData[key] = value;
        }
      }
      const forUpload = {
        ...irsScheduleC,
        form_data: {
          ...formData,
          net_profit_or_loss: toIntString(formData.net_profit_or_loss)
        }
      }

      response = (yield call(TaxApi.confirmDocument, forUpload)) as ApiResponse<SaveScheduleCResponse>;
      yield put(taxSlice.actions.confirmScheduleCSuccess({index: i, document: response.data}));
      i++;
    }

    const taxReturn = (response as ApiResponse<SaveScheduleCResponse>).data.tax_return;
    yield put(taxSlice.actions.setTaxReturn(taxReturn));
    yield put(taxSlice.actions.selectForm(-1));
    yield put(taxSlice.actions.proceedToNextStep({form: FormTypes.scheduleC, balance: true}));
  } catch (error: SerializedApiError<any> | any) {
    yield put(push("/upload/sorry", { state: { error: error } }));
  } finally {
    yield put(taxSlice.actions.removeTask(ConfirmScheduleCTask.id));
  }
}

function* delete1040Saga(): Generator<Effect, void, any> {
  yield put(taxSlice.actions.addTask(Delete1040Task));
  try {
    const irs1040 = (yield select(
      (state: RootState) => state.tax.irs1040)) as TaxDocument<IRS1040Form>;
    if (irs1040.uploaded_file.id !== null && irs1040.uploaded_file.id !== undefined) {
      const response = (yield call(TaxApi.deleteDocument, irs1040)) as AxiosResponse<Save1040Response>;
      yield put(taxSlice.actions.setTaxReturn(response.data.tax_return));
    }
    yield put(taxSlice.actions.delete1040Success());
  } catch (error) {
    yield put(taxSlice.actions.delete1040Error(String(error)));
  } finally {
    yield put(taxSlice.actions.removeTask(Delete1040Task.id));
  }
}

function* deleteW2Saga({ payload }: PayloadAction<number>): Generator<Effect, void, any> {
  yield put(taxSlice.actions.addTask(DeleteW2Task));
  try {
    const w2 = (yield select(
      (state: RootState) => state.tax.irsW2Forms[payload])) as TaxDocument<IRSW2Form>;
    if (w2.uploaded_file.id !== null && w2.uploaded_file.id !== undefined) {
      const response = (yield call(TaxApi.deleteDocument, w2)) as AxiosResponse<SaveW2CollectionResponse>;
      yield put(taxSlice.actions.setTaxReturn(response.data.tax_return));
    }
    yield put(taxSlice.actions.deleteW2Success(payload));
  } catch (error: SerializedApiError<any> | any) {
    yield put(taxSlice.actions.deleteW2Error(
      ApiError.deserialize(error)?.toString() ?? String(error)
    ));
  } finally {
    yield put(taxSlice.actions.removeTask(DeleteW2Task.id));
  }
}

function* deleteSchedule1Saga({ payload }: PayloadAction<number>): Generator<Effect, void, any> {
  yield put(taxSlice.actions.addTask(DeleteSchedule1Task));
  try {
    const sch1 = (yield select(
      (state: RootState) => state.tax.irsSchedule1Forms[payload])) as TaxDocument<IRSSchedule1Form>;
    if (sch1.uploaded_file.id !== null && sch1.uploaded_file.id !== undefined) {
      const response = (yield call(TaxApi.deleteDocument, sch1)) as AxiosResponse<SaveSchedule1CollectionResponse>;
      yield put(taxSlice.actions.setTaxReturn(response.data.tax_return));
    }
    yield put(taxSlice.actions.deleteSchedule1Success(payload));
  } catch (error: SerializedApiError<any> | any) {
    yield put(taxSlice.actions.deleteSchedule1Error(
      ApiError.deserialize(error)?.toString() ?? String(error)
    ));
  } finally {
    yield put(taxSlice.actions.removeTask(DeleteSchedule1Task.id));
  }
}

function* deleteScheduleCSaga({ payload }: PayloadAction<number>): Generator<Effect, void, any> {
  yield put(taxSlice.actions.addTask(DeleteScheduleCTask));
  try {
    const schC = (yield select(
      (state: RootState) => state.tax.irsScheduleCForms[payload])) as TaxDocument<IRSScheduleCForm>;
    if (schC.uploaded_file.id !== null && schC.uploaded_file.id !== undefined) {
      const response = (yield call(TaxApi.deleteDocument, schC)) as AxiosResponse<SaveScheduleCCollectionResponse>;
      yield put(taxSlice.actions.setTaxReturn(response.data.tax_return));
    }
    yield put(taxSlice.actions.deleteScheduleCSuccess(payload));
  } catch (error: SerializedApiError<any> | any) {
    yield put(taxSlice.actions.deleteScheduleCError(
      ApiError.deserialize(error)?.toString() ?? String(error)
    ));
  } finally {
    yield put(taxSlice.actions.removeTask(DeleteScheduleCTask.id));
  }
}
function* upload1040Saga({ payload }: PayloadAction<File>): Generator<Effect, void, any> {
  yield put(taxSlice.actions.addTask(Upload1040Task));
  try {
    const response = yield call(TaxApi.uploadFile, {
      file: payload,
      tax_form_type: FormTypes.t1040,
    });
    yield put(taxSlice.actions.upload1040Success({
      ...response.data,
      form_data: {
        ...response.data.form_data,
        other_income: response.data.form_data.other_income ?? "0"
      }
    }));
  } catch (error: SerializedApiError<any> | any) {
    yield put(taxSlice.actions.upload1040Error(
      ApiError.deserialize(error)?.toString() ?? String(error)
    ));
  } finally {
    yield put(taxSlice.actions.removeTask(Upload1040Task.id));
  }
}

function* uploadW2Saga({ payload }: PayloadAction<File>): Generator<Effect, void, any> {
  yield put(taxSlice.actions.addTask(UploadW2Task));
  try {
    const response = yield call(TaxApi.uploadFile, {
      file: payload,
      tax_form_type: FormTypes.w2,
    });
    yield put(taxSlice.actions.uploadW2Success(response.data));
  } catch (error: SerializedApiError<any> | any) {
    yield put(taxSlice.actions.uploadW2Error(
      ApiError.deserialize(error)?.toString() ?? String(error)
    ));
  } finally {
    yield put(taxSlice.actions.removeTask(UploadW2Task.id));
  }
}

function* uploadSchedule1Saga({ payload }: PayloadAction<File>): Generator<Effect, void, any> {
  yield put(taxSlice.actions.addTask(UploadSchedule1Task));
  try {
    const response = yield call(TaxApi.uploadFile, {
      file: payload,
      tax_form_type: FormTypes.schedule1,
    });
    yield put(taxSlice.actions.uploadSchedule1Success(response.data));
  } catch (error: SerializedApiError<any> | any) {
    yield put(taxSlice.actions.uploadSchedule1Error(
      ApiError.deserialize(error)?.toString() ?? String(error)
    ));
  } finally {
    yield put(taxSlice.actions.removeTask(UploadSchedule1Task.id));
  }
}

function* uploadScheduleCSaga({ payload }: PayloadAction<File>): Generator<Effect, void, any> {
  yield put(taxSlice.actions.addTask(UploadScheduleCTask));
  try {
    const response = yield call(TaxApi.uploadFile, {
      file: payload,
      tax_form_type: FormTypes.scheduleC,
    });
    yield put(taxSlice.actions.uploadScheduleCSuccess(response.data));
  } catch (error: SerializedApiError<any> | any) {
    yield put(taxSlice.actions.uploadScheduleCError(
      ApiError.deserialize(error)?.toString() ?? String(error)
    ));
  } finally {
    yield put(taxSlice.actions.removeTask(UploadScheduleCTask.id));
  }
}

// Note that we use takeLatest for the fetch calls,
// because React is running in Strict Mode and causes
// duplicate fetch actions to be dispatched. Using latest ensures
// that only one success or error action is dispatched
// instead of two.
export default function* taxSaga() {
  yield all([takeLatest(taxSlice.actions.fetchTaxReturnRequest.type, fetchTaxReturnSaga)]);
  // 1040
  yield all([takeEvery(taxSlice.actions.upload1040Request.type, upload1040Saga)]);
  yield all([
    takeLatest(taxSlice.actions.fetch1040FormDetails.type, fetch1040FormDetailsSaga),
  ]);
  yield all([
    takeEvery(taxSlice.actions.delete1040Request.type, delete1040Saga),
  ]);
  yield all([
    takeEvery(taxSlice.actions.validate1040Income.type, validate1040IncomeSaga),
  ]);
  yield all([
    takeEvery(taxSlice.actions.confirm1040Request.type, confirm1040Saga),
  ]);
  yield all([
    takeEvery(taxSlice.actions.confirmAllW2Request.type, confirmAllW2Saga),
  ]);
  yield all([
    takeEvery(taxSlice.actions.proceedToNextStep.type, proceeedToNextStepSaga),
  ]);

  // W2
  yield all([takeEvery(taxSlice.actions.uploadW2Request.type, uploadW2Saga)]);
  yield all([
    takeLatest(taxSlice.actions.fetchW2FormDetails.type, fetchW2FormDetailsSaga),
  ]);
  yield all([
    takeEvery(taxSlice.actions.deleteW2Request.type, deleteW2Saga),
  ]);
  // Schedule 1
  yield all([
    takeEvery(taxSlice.actions.uploadSchedule1Request.type, uploadSchedule1Saga),
  ]);
  yield all([
    takeLatest(taxSlice.actions.fetchSchedule1FormDetails.type, fetchSchedule1FormDetailsSaga),
  ]);
  yield all([
    takeEvery(
      taxSlice.actions.deleteSchedule1Request.type,
      deleteSchedule1Saga
    ),
  ]);
  yield all([
    takeEvery(
      taxSlice.actions.confirmAllSchedule1Request.type,
      confirmAllSchedule1Saga
    ),
  ]);
  // Schedule C
  yield all([
    takeEvery(taxSlice.actions.uploadScheduleCRequest.type, uploadScheduleCSaga),
  ]);
  yield all([
    takeLatest(taxSlice.actions.fetchScheduleCFormDetails.type, fetchScheduleCFormDetailsSaga),
  ]);
  yield all([
    takeEvery(taxSlice.actions.deleteScheduleCRequest, deleteScheduleCSaga),
  ]);
  yield all([
    takeEvery(taxSlice.actions.confirmAllScheduleCRequest.type, confirmAllScheduleCSaga),
  ]);
}
